import { FormProviderTypeUpdateParams } from "@/components/forms/form-editor.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { apiPerformanceReviews } from "@/api/performance-review.api.ts";
import { ApiOperationResult } from "@/types/common.types.ts";

const reviewerTypeFormKeys = createQueryKeys("reviewerTypeForms", {
  byId: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiPerformanceReviews.reviewerTypeFormGetById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  listByPerformanceReview: (performance_review_id: number) => ({
    queryKey: [performance_review_id],
    queryFn: async () => {
      const result = await apiPerformanceReviews.reviewerTypeFormGetAll(performance_review_id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useReviewerTypeFormById = (id: number, enabled = true) => {
  return useQuery({
    ...reviewerTypeFormKeys.byId(id),
    enabled: !!id && enabled,
  });
};

export const useReviewerTypeForms = (performance_review_id: number, enabled = true) => {
  return useQuery({
    ...reviewerTypeFormKeys.listByPerformanceReview(performance_review_id),
    enabled: !!performance_review_id && enabled,
  });
};

export const useReviewerTypeFormUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: FormProviderTypeUpdateParams) {
      const result = await apiPerformanceReviews.reviewerTypeFormUpdate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      // return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: reviewerTypeFormKeys.byId(variables.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: reviewerTypeFormKeys.listByPerformanceReview._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useReviewerTypeFormReset = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  options?: UseMutationOptions<ApiOperationResult, Error, { id: number }, unknown>
): UseMutationResult<ApiOperationResult, Error, { id: number }, unknown> => {
  const { onError } = useErrorHandle();

  return useMutation<ApiOperationResult, Error, { id: number }>({
    mutationFn: async ({ id }) => {
      const result = await apiPerformanceReviews.reviewerTypeFormReset(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onError: (error: Error) => {
      onError(error, control);
    },
    onSuccess: (data, vars, onMutateResult, ctx) => {
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
};
